/**
 * Translation Service using Backend Workflow
 * Posts to current URL with workflow data
 */
class TranslationService {
  constructor() {
    this.cacheKey = "translation_cache";
    this.cache = this.loadCache();
    this.maxTextLength = 5000;
  }

  loadCache() {
    try {
      const cached = siteStorage.get(this.cacheKey);
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  }

  saveCache() {
    try {
      siteStorage.set(this.cacheKey, JSON.stringify(this.cache));
    } catch (e) {
      // Silent fail
    }
  }

  getCacheKey(text, targetLang, sourceLang = "en") {
    return `${sourceLang}:${targetLang}:${text}`;
  }

  cleanText(text) {
    if (!text || typeof text !== "string") {
      return "";
    }

    text = text.trim();
    text = text.replace(/\s+/g, " ");

    if (text.length > this.maxTextLength) {
      text = text.substring(0, this.maxTextLength);
    }

    return text;
  }

  async translate(text, targetLang, sourceLang = "en") {
    text = this.cleanText(text);

    if (!text) {
      return text;
    }

    const cacheKey = this.getCacheKey(text, targetLang, sourceLang);
    if (this.cache[cacheKey]) {
      return this.cache[cacheKey];
    }

    try {
      const translations = await this.translateBatch(
        [text],
        targetLang,
        sourceLang,
      );
      return translations[0];
    } catch (error) {
      return text;
    }
  }

  async translateBatch(texts, targetLang, sourceLang = "en") {
    const textsToTranslate = [];
    const translations = new Array(texts.length);

    texts.forEach((text, index) => {
      const cleanedText = this.cleanText(text);

      if (!cleanedText) {
        translations[index] = text;
        return;
      }

      const cacheKey = this.getCacheKey(cleanedText, targetLang, sourceLang);
      if (this.cache[cacheKey]) {
        translations[index] = this.cache[cacheKey];
      } else {
        textsToTranslate.push({
          text: cleanedText,
          index,
          originalText: text,
        });
      }
    });

    if (textsToTranslate.length === 0) {
      return translations;
    }

    const batchSize = 50;
    const batches = [];

    for (let i = 0; i < textsToTranslate.length; i += batchSize) {
      batches.push(textsToTranslate.slice(i, i + batchSize));
    }

    for (const batch of batches) {
      const payload = {
        workflows: ["translate"],
        jinja_data: {
          translate_type: "batch",
          query: batch.map((item) => item.text),
          target_language: targetLang,
          source_language: sourceLang,
        },
      };

      try {
        const response = await fetch(window.location.href, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`Workflow error: ${response.status}`);
        }

        const result = await response.json();

        const workflowTranslations = result.data.translations.map(
          (t) => t.translatedText,
        );

        batch.forEach((item, i) => {
          const translatedText = workflowTranslations[i];
          translations[item.index] = translatedText;

          const cacheKey = this.getCacheKey(item.text, targetLang, sourceLang);
          this.cache[cacheKey] = translatedText;
        });

        this.saveCache();
      } catch (error) {
        for (const item of batch) {
          translations[item.index] = item.originalText;
        }
      }
    }

    return translations;
  }

  clearCache() {
    this.cache = {};
    siteStorage.remove(this.cacheKey);
  }

  getStats() {
    return {
      cachedItems: Object.keys(this.cache).length,
      cacheSize: new Blob([JSON.stringify(this.cache)]).size,
      endpoint: window.location.href,
    };
  }
}

window.translationService = new TranslationService();
